import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Activity, Shield, Eye, Navigation, GraduationCap } from 'lucide-react';
import { projectsData } from '../../data/projectsData';

const coreProductIds = ['vaidyamed-x', 'sudarshana-ai', 'trinetra-ai', 'roadresq-ai', 'sarathi-x'];

const getProductVisual = (id) => {
  switch (id) {
    case 'vaidyamed-x':
      return (
        <div className="w-full h-36 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/70 dark:border-slate-700/60 p-4 font-mono text-xs text-slate-600 dark:text-slate-300 flex flex-col justify-between overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200/60 dark:border-slate-700/60 pb-2">
            <span className="flex items-center gap-1.5 font-sans font-semibold text-emerald-600 dark:text-emerald-400 text-[11px]">
              <Activity size={13} /> Clinical RAG Query
            </span>
            <span className="text-[10px] bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-300 px-2 py-0.5 rounded font-mono">
              CONF: 98.4%
            </span>
          </div>
          <div className="space-y-1.5 py-1 text-[11px]">
            <p className="text-slate-800 dark:text-slate-200 font-sans font-medium">Query: Symptom triage & differential assessment</p>
            <p className="text-slate-500 font-mono text-[10px] truncate">&gt; Vector lookup: Indexed medical literature (FAISS)</p>
            <p className="text-emerald-700 dark:text-emerald-400 font-mono text-[10px]">&gt; Output: Triaged guidance + verified citations</p>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-400 pt-1 border-t border-slate-100 dark:border-slate-700/50">
            <span>Latency: 84ms</span>
            <span>•</span>
            <span>Zero-PII Storage</span>
          </div>
        </div>
      );
    case 'sudarshana-ai':
      return (
        <div className="w-full h-36 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/70 dark:border-slate-700/60 p-4 font-mono text-xs text-slate-600 dark:text-slate-300 flex flex-col justify-between overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200/60 dark:border-slate-700/60 pb-2">
            <span className="flex items-center gap-1.5 font-sans font-semibold text-brand-600 dark:text-brand-400 text-[11px]">
              <Shield size={13} /> Intrusion Telemetry
            </span>
            <span className="text-[10px] bg-brand-100 dark:bg-brand-900/60 text-brand-800 dark:text-brand-300 px-2 py-0.5 rounded font-mono">
              FILTER ACTIVE
            </span>
          </div>
          <div className="space-y-1.5 py-1 text-[11px]">
            <p className="text-slate-800 dark:text-slate-200 font-sans font-medium">Real-time packet inspection (eBPF Stream)</p>
            <p className="text-brand-700 dark:text-brand-400 font-mono text-[10px]">&gt; Anomaly score: 0.02 (Normal distribution)</p>
            <p className="text-slate-500 font-mono text-[10px]">&gt; Zero-trust auth tokens valid across nodes</p>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-400 pt-1 border-t border-slate-100 dark:border-slate-700/50">
            <span>Throughput: 10 Gbps</span>
            <span>•</span>
            <span>Auto-quarantine: Enabled</span>
          </div>
        </div>
      );
    case 'trinetra-ai':
      return (
        <div className="w-full h-36 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/70 dark:border-slate-700/60 p-4 font-mono text-xs text-slate-600 dark:text-slate-300 flex flex-col justify-between overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200/60 dark:border-slate-700/60 pb-2">
            <span className="flex items-center gap-1.5 font-sans font-semibold text-indigoBrand-600 dark:text-indigoBrand-400 text-[11px]">
              <Eye size={13} /> Deepfake Artifact Forensics
            </span>
            <span className="text-[10px] bg-indigo-100 dark:bg-indigo-900/60 text-indigo-800 dark:text-indigo-300 px-2 py-0.5 rounded font-mono">
              SCAN COMPLETED
            </span>
          </div>
          <div className="space-y-1.5 py-1 text-[11px]">
            <p className="text-slate-800 dark:text-slate-200 font-sans font-medium">Spectrogram & Spatial Consistency Analysis</p>
            <p className="text-indigo-700 dark:text-indigo-400 font-mono text-[10px]">&gt; Biometric eye-blink consistency: 99.1%</p>
            <p className="text-slate-500 font-mono text-[10px]">&gt; Acoustic voice print matched to ground truth</p>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-400 pt-1 border-t border-slate-100 dark:border-slate-700/50">
            <span>Video FPS: 60</span>
            <span>•</span>
            <span>Synthetic Likelihood: 0.8%</span>
          </div>
        </div>
      );
    case 'roadresq-ai':
      return (
        <div className="w-full h-36 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/70 dark:border-slate-700/60 p-4 font-mono text-xs text-slate-600 dark:text-slate-300 flex flex-col justify-between overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200/60 dark:border-slate-700/60 pb-2">
            <span className="flex items-center gap-1.5 font-sans font-semibold text-amber-600 dark:text-amber-400 text-[11px]">
              <Navigation size={13} /> Incident Triage & Dispatch
            </span>
            <span className="text-[10px] bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded font-mono">
              STANDBY READY
            </span>
          </div>
          <div className="space-y-1.5 py-1 text-[11px]">
            <p className="text-slate-800 dark:text-slate-200 font-sans font-medium">Vehicular Telematics Deceleration Event</p>
            <p className="text-amber-700 dark:text-amber-400 font-mono text-[10px]">&gt; Location coordinates parsed with GIS精度</p>
            <p className="text-slate-500 font-mono text-[10px]">&gt; Pre-cleared emergency corridor algorithm active</p>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-400 pt-1 border-t border-slate-100 dark:border-slate-700/50">
            <span>Response Delta: -40%</span>
            <span>•</span>
            <span>Direct SOS Relay</span>
          </div>
        </div>
      );
    case 'sarathi-x':
      return (
        <div className="w-full h-36 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/70 dark:border-slate-700/60 p-4 font-mono text-xs text-slate-600 dark:text-slate-300 flex flex-col justify-between overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200/60 dark:border-slate-700/60 pb-2">
            <span className="flex items-center gap-1.5 font-sans font-semibold text-purple-600 dark:text-purple-400 text-[11px]">
              <GraduationCap size={13} /> Cognitive Mastery Graph
            </span>
            <span className="text-[10px] bg-purple-100 dark:bg-purple-900/60 text-purple-800 dark:text-purple-300 px-2 py-0.5 rounded font-mono">
              DYNAMIC
            </span>
          </div>
          <div className="space-y-1.5 py-1 text-[11px]">
            <p className="text-slate-800 dark:text-slate-200 font-sans font-medium">Adaptive Code Evaluation & Diagnostic Sandbox</p>
            <p className="text-purple-700 dark:text-purple-400 font-mono text-[10px]">&gt; Real-time algorithmic complexity feedback</p>
            <p className="text-slate-500 font-mono text-[10px]">&gt; Tailored micro-challenges generated</p>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-400 pt-1 border-t border-slate-100 dark:border-slate-700/50">
            <span>Docker Sandbox</span>
            <span>•</span>
            <span>Skill Graph Tracking</span>
          </div>
        </div>
      );
    default:
      return null;
  }
};

const getCategoryBadgeClass = (id) => {
  switch (id) {
    case 'vaidyamed-x':
      return 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-300 border-emerald-200/60';
    case 'sudarshana-ai':
      return 'bg-brand-50 dark:bg-brand-950/50 text-brand-700 dark:text-brand-300 border-brand-200/60';
    case 'trinetra-ai':
      return 'bg-indigo-50 dark:bg-indigo-950/50 text-indigoBrand-600 dark:text-indigoBrand-300 border-indigo-200/60';
    case 'roadresq-ai':
      return 'bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border-amber-200/60';
    case 'sarathi-x':
      return 'bg-purple-50 dark:bg-purple-950/50 text-purple-700 dark:text-purple-300 border-purple-200/60';
    default:
      return 'bg-slate-50 text-slate-700 border-slate-200';
  }
};

const Projects = () => {
  const products = projectsData.filter((p) => coreProductIds.includes(p.id));

  return (
    <section id="products" className="py-24 relative bg-white dark:bg-slate-950">
      <div className="container mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-brand-50 dark:bg-brand-950/50 border border-brand-200/80 dark:border-brand-800 text-brand-700 dark:text-brand-300 text-xs font-semibold uppercase tracking-wider mb-4"
          >
            Engineering Portfolio
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="text-3xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4 tracking-tight"
          >
            Our Technology Products
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed"
          >
            Purpose-built technology platforms engineered for mission-critical reliability, deep analytics, and real-world deployment across high-impact industries.
          </motion.p>
        </div>

        {/* 5 Premium Product Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product, idx) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.08 }}
              className="flex"
            >
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-7 shadow-subtle hover:shadow-card hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-300 flex flex-col justify-between w-full group">
                <div>
                  {/* Top Header */}
                  <div className="flex items-center justify-between gap-3 mb-5">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide border ${getCategoryBadgeClass(product.id)}`}>
                      {product.domain}
                    </span>
                    {product.badge && (
                      <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                        {product.badge}
                      </span>
                    )}
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-2xl font-display font-bold text-slate-900 dark:text-white mb-3 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
                    {product.title}
                  </h3>

                  <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-6">
                    {product.shortDescription}
                  </p>

                  {/* Elegant UI Architecture Visualization */}
                  <div className="mb-6">
                    {getProductVisual(product.id)}
                  </div>

                  {/* Tech stack pills */}
                  <div className="flex flex-wrap gap-1.5 mb-6">
                    {product.techStack.slice(0, 4).map((tech, i) => (
                      <span 
                        key={i}
                        className="px-2.5 py-1 rounded-md text-[11px] font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-200/60 dark:border-slate-700"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Footer Action */}
                <div className="pt-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between">
                  <Link 
                    to={`/projects/${product.id}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 group/link transition-colors"
                  >
                    <span>Explore Product</span>
                    <ArrowRight size={16} className="group-hover/link:translate-x-1 transition-transform" />
                  </Link>

                  {product.demoLink && (
                    <a
                      href={product.demoLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                    >
                      Live Demo ↗
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
